import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Button,
    majorScale,
    Pane,
    Popover,
    Position as SettingsPosition,
    SettingsIcon,
    CogIcon,
    SmallCrossIcon,
    Tab,
    Tablist,
    ControlIcon,
    NotificationsIcon,
    VideoIcon,
} from 'evergreen-ui';
import styles from './TargetGroupSettings.module.css';
import { isValidMeetingUrl } from 'utils/meetingDomains';
import { useGetScanTemplateQuery } from '../../../store/api/scanTemplates';
import {
    Group,
    useGetGroupQuery,
    useUpdateGroupConfigMutation,
} from '../../../store/api/groups';
import { GeneralTab } from './tabs/GeneralTab';
import { SettingsTab } from './tabs/SettingsTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { MotiviewTab } from './tabs/MotiviewTab';

interface TargetGroupSettingsProps {
    assessmentId: string;
    groupId: string;
    registrationUrl: string;
    companySlug: string;
    isSuperAdmin: boolean;
}

/**
 * Settings popover for a target group. Owns:
 *   - all form state for every tab
 *   - the data load effect (`setFieldsFromState`)
 *   - the save handlers (one per editable tab, each scoped to its own fields
 *     so saving from one tab cannot overwrite another tab's state)
 *   - the shared "Saved!" feedback flow
 *
 * Each tab is a self-contained subcomponent that receives the props it needs.
 *
 * The dedicated Motiview tab only appears for SNAP_FIT groups under a super
 * admin. Position 4 (after Notifications) so existing tab indices don't shift
 * when the group type loads.
 */
export const TargetGroupSettings = (props: TargetGroupSettingsProps) => {
    const {
        assessmentId,
        groupId,
        companySlug,
        registrationUrl,
        isSuperAdmin,
    } = props;

    const { data: group } = useGetGroupQuery({
        companyIdentifier: companySlug,
        groupId: groupId,
    });

    const { data: scanTemplate } = useGetScanTemplateQuery({
        companyIdentifier: companySlug,
        scanTemplateId: assessmentId,
    });

    const [updateGroupConfig, { isLoading, error }] =
        useUpdateGroupConfigMutation();

    const { name: scanName, language } = scanTemplate ?? {
        name: '',
        language: '',
    };

    const isMotiviewTabVisible = isSuperAdmin && group?.type === 'SNAP_FIT';
    const tabs = isMotiviewTabVisible
        ? ['General', 'Settings', 'Notifications', 'Motiview']
        : ['General', 'Settings', 'Notifications'];

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [displaySuccess, setDisplaySuccess] = useState<boolean>(false);
    const [savingResultTimeoutId, setSavingResultTimeoutId] =
        useState<NodeJS.Timeout>();
    // The load effect (further down) hydrates local state from `group`. We only
    // want it to run ONCE — on the initial load when the modal opens. After
    // that, local state is the source of truth and any subsequent `group`
    // changes (e.g. from the cache invalidation triggered by our own save
    // mutation) must NOT overwrite local state, otherwise unsaved edits in
    // OTHER tabs would be silently wiped during a partial save flow.
    //
    // The modal close handler still calls setFieldsFromState explicitly to
    // reset the form to the current DB state when the user closes the popover.
    const hasHydratedFromGroup = useRef(false);

    // Settings tab fields
    const [targetGroupName, setTargetGroupName] = useState<string>('');
    const [targetGroupDescription, setTargetGroupDescription] =
        useState<string>('');
    const [isTargetGroupPublic, setIsTargetGroupPublic] =
        useState<boolean>(false);
    const [verifyEmails, setVerifyEmails] = useState<boolean>(true);
    const [autoSendReportEmails, setAutoSendReportEmails] =
        useState<boolean>(false);
    const [hideCompanyBaselineInPdf, setHideCompanyBaselineInPdf] =
        useState<boolean>(false);

    // Notifications tab fields
    const [scanCompletionNotification, setScanCompletionNotification] =
        useState<boolean>(true);
    const [
        scanCompletionNotificationEmails,
        setScanCompletionNotificationEmails,
    ] = useState<string>('');
    const [
        isScanCompletionNotificationEmailsValid,
        setScanCompletionNotificationEmailsValid,
    ] = useState(false);
    const [reportGenerationNotification, setReportGenerationNotification] =
        useState<boolean>(true);
    const [
        reportGenerationNotificationEmails,
        setReportGenerationNotificationEmails,
    ] = useState<string>('');
    const [
        isReportGenerationNotificationEmailsValid,
        setReportGenerationNotificationEmailsValid,
    ] = useState(false);

    // Motiview tab fields
    const [allowSnapFitRegistration, setAllowSnapFitRegistration] =
        useState<boolean>(false);
    const [isWebsiteDemo, setIsWebsiteDemo] = useState<boolean>(false);
    const [allowRepeatMotiviewScan, setAllowRepeatMotiviewScan] =
        useState<boolean>(false);
    const [
        showMotiviewDetailsFormBeforeResult,
        setShowMotiviewDetailsFormBeforeResult,
    ] = useState<boolean>(false);
    const [
        showMotiviewDetailsFormAfterResult,
        setShowMotiviewDetailsFormAfterResult,
    ] = useState<boolean>(false);
    const [showCompanyLogo, setShowCompanyLogo] = useState<boolean>(false);
    const [allowMeetingAboveAverage, setAllowMeetingAboveAverage] =
        useState<boolean>(false);
    const [allowMeetingAverage, setAllowMeetingAverage] =
        useState<boolean>(false);
    const [allowMeetingBelowAverage, setAllowMeetingBelowAverage] =
        useState<boolean>(false);
    const [meetingUrls, setMeetingUrls] = useState<string[]>([]);

    // Hydrate every form field from the loaded group payload. Called on the
    // initial group load and on modal close (to discard unsaved local edits
    // and reset the form to the current DB state). NOT called after a save —
    // see the save handlers below for the rationale.
    const setFieldsFromState = useCallback((updatedGroup: Group) => {
        if (!updatedGroup) return;
        setTargetGroupName(updatedGroup.name);
        setTargetGroupDescription(updatedGroup.description ?? '');
        setIsTargetGroupPublic(updatedGroup.config?.is_public ?? false);
        setVerifyEmails(
            typeof updatedGroup.config?.verify_emails === 'boolean'
                ? updatedGroup.config.verify_emails
                : true,
        );
        setAutoSendReportEmails(
            updatedGroup.config?.auto_send_reports ?? false,
        );
        setHideCompanyBaselineInPdf(
            updatedGroup.config?.hide_company_baseline_in_pdf ?? false,
        );
        setScanCompletionNotification(
            typeof updatedGroup.config?.scan_completion_notification ===
                'boolean'
                ? updatedGroup.config.scan_completion_notification
                : false,
        );
        setScanCompletionNotificationEmails(
            updatedGroup.config?.scan_completion_notification_emails ?? '',
        );
        setReportGenerationNotification(
            typeof updatedGroup.config?.report_generation_notification ===
                'boolean'
                ? updatedGroup.config.report_generation_notification
                : false,
        );
        setReportGenerationNotificationEmails(
            updatedGroup.config?.report_generation_notification_emails ?? '',
        );
        setAllowSnapFitRegistration(
            updatedGroup.snap_fit_config?.allow_registration ?? false,
        );
        setIsWebsiteDemo(
            updatedGroup.snap_fit_config?.is_website_demo ?? false,
        );
        setAllowRepeatMotiviewScan(
            updatedGroup.snap_fit_config?.allow_repeat_motiview_scan ?? false,
        );
        setShowMotiviewDetailsFormBeforeResult(
            updatedGroup.snap_fit_config
                ?.show_motiview_details_form_before_result ?? false,
        );
        setShowMotiviewDetailsFormAfterResult(
            updatedGroup.snap_fit_config
                ?.show_motiview_details_form_after_result ?? false,
        );
        setAllowMeetingAboveAverage(
            updatedGroup.snap_fit_config?.allow_meeting_above_average ?? false,
        );
        setAllowMeetingAverage(
            updatedGroup.snap_fit_config?.allow_meeting_average ?? false,
        );
        setAllowMeetingBelowAverage(
            updatedGroup.snap_fit_config?.allow_meeting_below_average ?? false,
        );
        setMeetingUrls(updatedGroup.snap_fit_config?.meeting_urls ?? []);
        setShowCompanyLogo(
            updatedGroup.snap_fit_config?.show_company_logo ?? false,
        );
    }, []);

    // IMPORTANT — about save isolation:
    // Each save handler sends ONLY the fields owned by its tab. The backend's
    // PUT /config endpoint preserves any field omitted from the body, so saving
    // from one tab never touches another tab's stored state.
    //
    // We deliberately do NOT call setFieldsFromState after a save: local state
    // already reflects what the user typed/toggled and what we just sent to the
    // backend, and rehydrating from the full response would wipe unsaved
    // changes in OTHER tabs (e.g., user types a new Name in Settings, switches
    // to Motiview, toggles a flag, hits Save — the response carries the old
    // Name from the DB, and a global rehydration would silently revert the
    // unsaved Name change). The reactive `[isLoading, error]` useEffect still
    // shows the "Saved!" indicator on completion, so removing the .then chain
    // does not affect the success UX.

    const onSettingsSave = () => {
        setIsSaving(true);
        updateGroupConfig({
            companyIdentifier: companySlug,
            groupId,
            groupConfig: {
                name: targetGroupName,
                description: targetGroupDescription,
                isPublic: isTargetGroupPublic,
                verifyEmails: verifyEmails,
                autoSendReports: autoSendReportEmails,
                // Only super admins ever see / can flip this toggle in the UI;
                // for any other role the field is omitted so saving the tab
                // doesn't accidentally rewrite the flag.
                ...(isSuperAdmin ? { hideCompanyBaselineInPdf } : {}),
            },
        });
    };

    const onNotificationsSave = () => {
        setIsSaving(true);
        updateGroupConfig({
            companyIdentifier: companySlug,
            groupId,
            groupConfig: {
                scanCompletionNotification,
                scanCompletionNotificationEmails,
                reportGenerationNotification,
                reportGenerationNotificationEmails,
            },
        });
    };

    const onMotiviewSave = () => {
        setIsSaving(true);
        // If every meeting flag is off, persist an empty meeting_urls list.
        // Without this, the URLs the admin entered earlier would linger in
        // the backend even though no flag would ever surface them, leading
        // to confusing state if a flag is later re-enabled.
        const anyMeetingFlag =
            allowMeetingAboveAverage ||
            allowMeetingAverage ||
            allowMeetingBelowAverage;
        updateGroupConfig({
            companyIdentifier: companySlug,
            groupId,
            groupConfig: {
                allowSnapFitRegistration,
                isWebsiteDemo,
                allowRepeatMotiviewScan,
                showMotiviewDetailsFormBeforeResult,
                showMotiviewDetailsFormAfterResult,
                allowMeetingAboveAverage,
                allowMeetingAverage,
                allowMeetingBelowAverage,
                meetingUrls: anyMeetingFlag
                    ? meetingUrls.filter((u) => isValidMeetingUrl(u.trim()))
                    : [],
                showCompanyLogo,
            },
        });
    };

    const onScanCompletionEmailsChange = (emails: string, isValid: boolean) => {
        setScanCompletionNotificationEmails(emails);
        setScanCompletionNotificationEmailsValid(
            isValid || (!scanCompletionNotification && emails === ''),
        );
    };

    const onReportGenerationEmailsChange = (
        emails: string,
        isValid: boolean,
    ) => {
        setReportGenerationNotificationEmails(emails);
        setReportGenerationNotificationEmailsValid(
            isValid || (!reportGenerationNotification && emails === ''),
        );
    };

    const onSettingsClosed = () => {
        if (group) {
            setFieldsFromState(group);
        }
        setSelectedIndex(0);
    };

    // Hydrate local form state from `group` exactly ONCE — on the initial load
    // when the modal opens. After that, local state is the source of truth and
    // any subsequent `group` changes (e.g. from the cache invalidation that the
    // updateGroupConfig mutation triggers via invalidatesTags) must NOT
    // overwrite local state, otherwise unsaved edits in OTHER tabs would be
    // silently wiped during a partial save flow. The user explicitly asked for
    // each tab's save to be fully isolated.
    //
    // The modal-close handler still calls setFieldsFromState explicitly, so
    // closing the popover (without saving) discards local edits and resets the
    // form to the current DB state — which matches the original UX.
    useEffect(() => {
        if (!group) return;
        if (hasHydratedFromGroup.current) return;
        setFieldsFromState(group);
        hasHydratedFromGroup.current = true;
    }, [setFieldsFromState, group]);

    useEffect(() => {
        if (isSaving && !isLoading && !error) {
            if (savingResultTimeoutId) {
                clearTimeout(savingResultTimeoutId);
            }
            setIsSaving(false);
            setDisplaySuccess(true);
            const timeoutId = setTimeout(() => setDisplaySuccess(false), 5000);
            setSavingResultTimeoutId(timeoutId);
        } else {
            setDisplaySuccess(false);
        }
    }, [isLoading, error]); // eslint-disable-line react-hooks/exhaustive-deps

    const renderTabIcon = (tab: string) => {
        switch (tab) {
            case 'General':
                return <ControlIcon className={styles.tab_list_icon} />;
            case 'Settings':
                return <CogIcon className={styles.tab_list_icon} />;
            case 'Notifications':
                return <NotificationsIcon className={styles.tab_list_icon} />;
            case 'Motiview':
                return <VideoIcon className={styles.tab_list_icon} />;
            default:
                return null;
        }
    };

    const renderTabPanel = (tab: string, index: number) => {
        const sharedPanelProps = { tab, index, selectedIndex };
        switch (tab) {
            case 'Settings':
                return (
                    <SettingsTab
                        key={tab}
                        {...sharedPanelProps}
                        targetGroupName={targetGroupName}
                        setTargetGroupName={setTargetGroupName}
                        targetGroupDescription={targetGroupDescription}
                        setTargetGroupDescription={setTargetGroupDescription}
                        isTargetGroupPublic={isTargetGroupPublic}
                        setIsTargetGroupPublic={setIsTargetGroupPublic}
                        registrationUrl={registrationUrl}
                        verifyEmails={verifyEmails}
                        setVerifyEmails={setVerifyEmails}
                        autoSendReportEmails={autoSendReportEmails}
                        setAutoSendReportEmails={setAutoSendReportEmails}
                        isSuperAdmin={isSuperAdmin}
                        hideCompanyBaselineInPdf={hideCompanyBaselineInPdf}
                        setHideCompanyBaselineInPdf={
                            setHideCompanyBaselineInPdf
                        }
                        isLoading={isLoading}
                        displaySuccess={displaySuccess}
                        onSave={onSettingsSave}
                    />
                );
            case 'Notifications':
                return (
                    <NotificationsTab
                        key={tab}
                        {...sharedPanelProps}
                        scanCompletionNotification={scanCompletionNotification}
                        setScanCompletionNotification={
                            setScanCompletionNotification
                        }
                        scanCompletionNotificationEmails={
                            scanCompletionNotificationEmails
                        }
                        isScanCompletionNotificationEmailsValid={
                            isScanCompletionNotificationEmailsValid
                        }
                        onScanCompletionEmailsChange={
                            onScanCompletionEmailsChange
                        }
                        reportGenerationNotification={
                            reportGenerationNotification
                        }
                        setReportGenerationNotification={
                            setReportGenerationNotification
                        }
                        reportGenerationNotificationEmails={
                            reportGenerationNotificationEmails
                        }
                        isReportGenerationNotificationEmailsValid={
                            isReportGenerationNotificationEmailsValid
                        }
                        onReportGenerationEmailsChange={
                            onReportGenerationEmailsChange
                        }
                        isLoading={isLoading}
                        displaySuccess={displaySuccess}
                        onSave={onNotificationsSave}
                    />
                );
            case 'Motiview':
                return (
                    <MotiviewTab
                        key={tab}
                        {...sharedPanelProps}
                        allowSnapFitRegistration={allowSnapFitRegistration}
                        setAllowSnapFitRegistration={
                            setAllowSnapFitRegistration
                        }
                        isWebsiteDemo={isWebsiteDemo}
                        setIsWebsiteDemo={setIsWebsiteDemo}
                        allowRepeatMotiviewScan={allowRepeatMotiviewScan}
                        setAllowRepeatMotiviewScan={setAllowRepeatMotiviewScan}
                        showMotiviewDetailsFormBeforeResult={
                            showMotiviewDetailsFormBeforeResult
                        }
                        setShowMotiviewDetailsFormBeforeResult={
                            setShowMotiviewDetailsFormBeforeResult
                        }
                        showMotiviewDetailsFormAfterResult={
                            showMotiviewDetailsFormAfterResult
                        }
                        setShowMotiviewDetailsFormAfterResult={
                            setShowMotiviewDetailsFormAfterResult
                        }
                        showCompanyLogo={showCompanyLogo}
                        setShowCompanyLogo={setShowCompanyLogo}
                        allowMeetingAboveAverage={allowMeetingAboveAverage}
                        setAllowMeetingAboveAverage={
                            setAllowMeetingAboveAverage
                        }
                        allowMeetingAverage={allowMeetingAverage}
                        setAllowMeetingAverage={setAllowMeetingAverage}
                        allowMeetingBelowAverage={allowMeetingBelowAverage}
                        setAllowMeetingBelowAverage={
                            setAllowMeetingBelowAverage
                        }
                        meetingUrls={meetingUrls}
                        setMeetingUrls={setMeetingUrls}
                        isLoading={isLoading}
                        displaySuccess={displaySuccess}
                        onSave={onMotiviewSave}
                    />
                );
            default:
                return (
                    <GeneralTab
                        key={tab}
                        {...sharedPanelProps}
                        companySlug={companySlug}
                        assessmentId={assessmentId}
                        scanName={scanName}
                        language={language}
                        isSuperAdmin={isSuperAdmin}
                        group={group}
                    />
                );
        }
    };

    return (
        <div className={styles.target_group_settings}>
            <Popover
                position={SettingsPosition.TOP_LEFT}
                shouldCloseOnExternalClick={false}
                onClose={onSettingsClosed}
                // @ts-ignore
                content={({ close }: { close: () => void }) => (
                    <>
                        <div
                            className={
                                styles.target_group_settings__pane__close_icon
                            }>
                            <SmallCrossIcon size={19} onClick={close} />
                        </div>
                        <Pane
                            maxWidth="min(60vw, 1100px)"
                            maxHeight="70vh"
                            overflowY="auto"
                            display="flex"
                            padding={majorScale(2)}>
                            <Tablist marginY={22} marginX={18} flexBasis={130}>
                                {tabs.map((tab, index) => (
                                    <Tab
                                        key={tab}
                                        id={tab}
                                        direction="vertical"
                                        onSelect={() => setSelectedIndex(index)}
                                        isSelected={index === selectedIndex}
                                        aria-controls={`panel-${tab}`}
                                        className={
                                            styles.target_group_settings__configuration_tab
                                        }>
                                        {renderTabIcon(tab)}
                                        {tab}
                                    </Tab>
                                ))}
                            </Tablist>
                            <Pane padding={22} flex="1">
                                {tabs.map((tab, index) =>
                                    renderTabPanel(tab, index),
                                )}
                            </Pane>
                        </Pane>
                    </>
                )}>
                <Button style={{ height: '35px' }}>
                    <SettingsIcon size={20} />
                </Button>
            </Popover>
        </div>
    );
};

export default TargetGroupSettings;
